/**
 * Enemy lie art plates: /art/lies/{defId}-{1|2|3}.webp
 * Stage 3 = strongest claim; stage 1 = residual.
 * Campaign skins may use a separate artId; missing plates fall back to base family.
 */

import {
  bindArtImage,
  isArtKnownMissing,
  markArtMissing,
  markArtOk,
} from "./artCache";

export function lieArtStage(level: number): 1 | 2 | 3 {
  const n = Math.round(level);
  if (n >= 3) return 3;
  if (n <= 1) return 1;
  return 2;
}

export function lieArtSrc(defId: string, level: number): string {
  return `/art/lies/${defId}-${lieArtStage(level)}.webp`;
}

function pickSrc(
  preferredId: string,
  level: number,
  fallbackId?: string,
): { src: string; usedId: string } {
  const preferred = lieArtSrc(preferredId, level);
  if (!fallbackId || fallbackId === preferredId) {
    return { src: preferred, usedId: preferredId };
  }
  if (!isArtKnownMissing(preferred)) {
    return { src: preferred, usedId: preferredId };
  }
  return { src: lieArtSrc(fallbackId, level), usedId: fallbackId };
}

/** Build a compact art frame for board presence / inspect. */
export function createLieArt(opts: {
  defId: string;
  level: number;
  name?: string;
  /** Larger frame for inspect modal */
  large?: boolean;
  /** When preferred art is missing, try this family (mechanical lie id). */
  fallbackDefId?: string;
}): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "lie-art" + (opts.large ? " lie-art-large" : "");
  const preferred = opts.defId;
  const fallback = opts.fallbackDefId ?? preferred;
  const { src, usedId } = pickSrc(preferred, opts.level, fallback);
  wrap.dataset.defId = usedId;
  wrap.dataset.preferredId = preferred;
  wrap.dataset.fallbackId = fallback;
  wrap.dataset.stage = String(lieArtStage(opts.level));

  const placeholder = document.createElement("div");
  placeholder.className = "lie-art-placeholder";
  placeholder.setAttribute("aria-hidden", "true");
  const mono = document.createElement("span");
  mono.className = "lie-art-monogram";
  mono.textContent = monogramFromLieId(preferred);
  placeholder.appendChild(mono);
  wrap.appendChild(placeholder);

  if (isArtKnownMissing(src)) {
    wrap.classList.add("no-art");
    return wrap;
  }

  const img = document.createElement("img");
  img.className = "lie-art-img";
  img.alt = opts.name ? `${opts.name} art` : "";
  img.loading = "lazy";
  img.decoding = "async";
  img.draggable = false;

  const onMissing = (): void => {
    if (usedId === preferred && fallback !== preferred) {
      const fbSrc = lieArtSrc(fallback, opts.level);
      if (!isArtKnownMissing(fbSrc)) {
        wrap.dataset.defId = fallback;
        bindArtImage(img, fbSrc, () => {
          img.remove();
          wrap.classList.add("no-art");
        });
        return;
      }
    }
    img.remove();
    wrap.classList.add("no-art");
  };

  bindArtImage(img, src, onMissing);
  wrap.appendChild(img);
  return wrap;
}

/** Swap plate when intensity steps down/up without rebuilding the card. */
export function updateLieArtEl(
  root: HTMLElement,
  defId: string,
  level: number,
  fallbackDefId?: string,
): void {
  const art = root.querySelector(".lie-art") as HTMLElement | null;
  if (!art) return;
  const stage = lieArtStage(level);
  const preferred = defId;
  const fallback =
    fallbackDefId ?? art.dataset.fallbackId ?? preferred;
  const { src, usedId } = pickSrc(preferred, level, fallback);

  if (
    art.dataset.stage === String(stage) &&
    art.dataset.defId === usedId &&
    art.dataset.preferredId === preferred
  ) {
    return;
  }

  art.dataset.preferredId = preferred;
  art.dataset.fallbackId = fallback;
  art.dataset.defId = usedId;
  art.dataset.stage = String(stage);

  const ensureImg = (): HTMLImageElement => {
    let img = art.querySelector(".lie-art-img") as HTMLImageElement | null;
    if (!img) {
      img = document.createElement("img");
      img.className = "lie-art-img";
      img.alt = "";
      img.loading = "lazy";
      img.decoding = "async";
      img.draggable = false;
      art.appendChild(img);
    }
    return img;
  };

  const fail = (): void => {
    if (usedId === preferred && fallback !== preferred) {
      const fbSrc = lieArtSrc(fallback, level);
      if (!isArtKnownMissing(fbSrc)) {
        art.dataset.defId = fallback;
        art.classList.remove("no-art");
        const img = ensureImg();
        bindArtImage(img, fbSrc, () => {
          img.remove();
          art.classList.add("no-art");
        });
        return;
      }
    }
    art.querySelector(".lie-art-img")?.remove();
    art.classList.add("no-art");
  };

  if (isArtKnownMissing(src)) {
    fail();
    return;
  }

  art.classList.remove("no-art");
  const img = ensureImg();
  bindArtImage(img, src, fail);
}

function monogramFromLieId(id: string): string {
  const parts = id.split("-").filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  }
  return id.replace(/[^a-z]/gi, "").slice(0, 2).toUpperCase() || "L";
}

/** Preload shipped plates so spawn does not flash empty. */
export function preloadLieArt(
  defIds: string[],
  levels: number[] = [1, 2, 3],
): void {
  for (const id of defIds) {
    for (const lv of levels) {
      const src = lieArtSrc(id, lv);
      if (isArtKnownMissing(src)) continue;
      const img = new Image();
      img.onload = () => markArtOk(src);
      img.onerror = () => markArtMissing(src);
      img.src = src;
    }
  }
}
